/**
 * Registre des documents juridiques — une source unique pour les routes,
 * le footer, le sitemap et le SEO.
 *
 * Les documents eux-mêmes sont dans les fichiers voisins et ne se
 * modifient pas. Ce fichier ne fait que : (1) résoudre les jetons {{…}},
 * (2) attacher à chaque document sa route, son titre de page et sa
 * description meta, (3) servir la bonne VERSION LINGUISTIQUE.
 *
 * TROIS LANGUES, UNE SEULE ROUTE
 * ──────────────────────────────
 * `/conditions-generales` sert le français, le néerlandais ou l'anglais
 * selon la langue du site — la route ne change pas. C'est délibéré :
 * préfixer l'URL (`/nl/algemene-voorwaarden`) multiplierait par trois les
 * liens du footer, les canoniques et les entrées de sitemap pour des
 * documents dont le contenu fait foi en français de toute façon.
 *
 * La version française PRÉVAUT en cas de divergence — chaque document le
 * dit en clair, dans les trois langues, sous sa date de mise à jour. Ce
 * n'est pas une formule de style : c'est ce qui permet de servir une
 * traduction sans créer trois contrats différents.
 *
 * Un document manquant dans une langue retombe sur le français plutôt que
 * de rendre une page vide (`docsFor`). Aujourd'hui les douze existent.
 */
import CGV_FR from './cgv.fr';
import CGV_NL from './cgv.nl';
import CGV_EN from './cgv.en';
import CONFIDENTIALITE_FR from './confidentialite.fr';
import CONFIDENTIALITE_NL from './confidentialite.nl';
import CONFIDENTIALITE_EN from './confidentialite.en';
import COOKIES_FR from './cookies.fr';
import COOKIES_NL from './cookies.nl';
import COOKIES_EN from './cookies.en';
import MENTIONS_LEGALES_FR from './mentions-legales.fr';
import MENTIONS_LEGALES_NL from './mentions-legales.nl';
import MENTIONS_LEGALES_EN from './mentions-legales.en';

/**
 * Date de mise en ligne des quatre documents. Elle est FIGÉE : c'est la
 * date de la version validée, pas « aujourd'hui ». Un `new Date()` ici
 * ferait bouger la « dernière mise à jour » d'un document juridique à
 * chaque chargement de page — exactement ce qu'une date de version ne
 * doit jamais faire. À changer à la main lors de la prochaine révision.
 */
export const DATE_MISE_EN_LIGNE = '9 août 2026';

/**
 * Durée du cookie de session, telle qu'elle est RÉELLEMENT posée par le
 * backend :
 *   backend/auth.py  SESSION_DAYS_DEFAULT  = 7
 *   backend/auth.py  SESSION_DAYS_REMEMBER = 30
 *
 * DEUX durées depuis que l'écran de connexion porte une case « Se
 * souvenir de moi ». Décochée — le défaut —, la session vaut 7 jours et
 * le cookie n'est PAS persistant : il disparaît à la fermeture du
 * navigateur. Cochée, la session et le cookie valent 30 jours.
 *
 * Annoncer « 7 jours » tout court serait désormais faux pour quiconque
 * coche la case, et une politique cookies fausse est un problème
 * juridique, pas un détail de rédaction. Si l'une des deux durées change
 * côté backend, elle change ici.
 *
 * Une écriture par langue : le jeton est inséré dans une CELLULE de
 * tableau (« Session ou {{DUREE_SESSION}} »), pas dans un paragraphe, et
 * une durée en français au milieu d'un tableau néerlandais est le genre
 * de détail qui fait douter du reste du document.
 */
const DUREE_SESSION_PAR_LANGUE = {
  fr: '7 jours (30 jours avec « Se souvenir de moi »)',
  nl: '7 dagen (30 dagen met « Aangemeld blijven »)',
  en: '7 days (30 days with "Remember me")',
};

/** Compat : les appelants historiques lisent la version française. */
export const DUREE_SESSION = DUREE_SESSION_PAR_LANGUE.fr;

/** Les trois langues servies. Le français est le repli et fait foi. */
export const LEGAL_LANGS = ['fr', 'nl', 'en'];
export const DEFAULT_LEGAL_LANG = 'fr';

export function normalizeLegalLang(lang) {
  const l = String(lang || DEFAULT_LEGAL_LANG).slice(0, 2).toLowerCase();
  return LEGAL_LANGS.includes(l) ? l : DEFAULT_LEGAL_LANG;
}

const fill = (md, lang) => String(md)
  .replace(/\{\{DATE_MISE_EN_LIGNE\}\}/g, DATE_MISE_EN_LIGNE)
  .replace(/\{\{DUREE_SESSION\}\}/g, DUREE_SESSION_PAR_LANGUE[lang] || DUREE_SESSION_PAR_LANGUE.fr);

/**
 * Les quatre documents, en trois langues. `slug` est la route (sans le
 * « / ») et NE VARIE PAS selon la langue ; `toc` demande la table des
 * matières ancrée en haut de page (CGV seules : quatorze sections, c'est
 * le seul document qui se parcourt).
 *
 * `label`, `title` et `description` sont traduits : ce sont eux qui
 * partent dans le `<title>`, la meta description et les liens croisés en
 * bas de page. Servir un document néerlandais sous un titre français
 * était le défaut le plus visible de la version précédente.
 */
const DOCS = {
  'conditions-generales': {
    toc: true,
    fr: {
      label: 'Conditions générales',
      title: 'Conditions générales de vente et d’utilisation',
      description: 'Les conditions générales d’Archer : offres, abonnements, prix, droit de rétractation, responsabilité et droit applicable. Droit belge.',
      markdown: CGV_FR,
    },
    nl: {
      label: 'Algemene voorwaarden',
      title: 'Algemene voorwaarden voor verkoop en gebruik',
      description: 'De algemene voorwaarden van Archer: aanbod, abonnementen, prijzen, herroepingsrecht, aansprakelijkheid en toepasselijk recht. Belgisch recht.',
      markdown: CGV_NL,
    },
    en: {
      label: 'Terms and conditions',
      title: 'General Terms and Conditions of Sale and Use',
      description: 'Archer’s general terms: plans, subscriptions, prices, right of withdrawal, liability and governing law. Belgian law.',
      markdown: CGV_EN,
    },
  },
  confidentialite: {
    toc: false,
    fr: {
      label: 'Confidentialité',
      title: 'Politique de confidentialité',
      description: 'Quelles données personnelles Archer traite, pourquoi, combien de temps, avec quels sous-traitants, et comment exercer vos droits RGPD.',
      markdown: CONFIDENTIALITE_FR,
    },
    nl: {
      label: 'Privacy',
      title: 'Privacybeleid',
      description: 'Welke persoonsgegevens Archer verwerkt, waarom, hoe lang, met welke verwerkers, en hoe u uw AVG-rechten uitoefent.',
      markdown: CONFIDENTIALITE_NL,
    },
    en: {
      label: 'Privacy',
      title: 'Privacy policy',
      description: 'Which personal data Archer processes, why, for how long, with which processors, and how to exercise your GDPR rights.',
      markdown: CONFIDENTIALITE_EN,
    },
  },
  cookies: {
    toc: false,
    fr: {
      label: 'Cookies',
      title: 'Politique cookies',
      description: 'Les cookies utilisés par Archer : strictement nécessaires, et traceurs publicitaires soumis à votre consentement préalable. Comment changer votre choix.',
      markdown: COOKIES_FR,
    },
    nl: {
      label: 'Cookies',
      title: 'Cookiebeleid',
      description: 'De cookies die Archer gebruikt: strikt noodzakelijke cookies, en reclametrackers onderworpen aan uw voorafgaande toestemming. Hoe u uw keuze wijzigt.',
      markdown: COOKIES_NL,
    },
    en: {
      label: 'Cookies',
      title: 'Cookie policy',
      description: 'The cookies Archer uses: strictly necessary ones, and advertising trackers subject to your prior consent. How to change your choice.',
      markdown: COOKIES_EN,
    },
  },
  'mentions-legales': {
    toc: false,
    fr: {
      label: 'Mentions légales',
      title: 'Mentions légales',
      description: 'Éditeur, responsable de la publication, hébergeur, nature du service et propriété intellectuelle du site archer.law.',
      markdown: MENTIONS_LEGALES_FR,
    },
    nl: {
      label: 'Wettelijke vermeldingen',
      title: 'Wettelijke vermeldingen',
      description: 'Uitgever, verantwoordelijke uitgever, host, aard van de dienst en intellectuele eigendom van de site archer.law.',
      markdown: MENTIONS_LEGALES_NL,
    },
    en: {
      label: 'Legal notice',
      title: 'Legal notice',
      description: 'Publisher, editor in charge, host, nature of the service and intellectual property of the archer.law site.',
      markdown: MENTIONS_LEGALES_EN,
    },
  },
};

/** Les quatre slugs, dans l'ordre d'affichage du footer. */
export const LEGAL_SLUGS = Object.keys(DOCS);

/** Les quatre documents dans une langue donnée, jetons résolus. */
export function docsFor(lang) {
  const l = normalizeLegalLang(lang);
  return LEGAL_SLUGS.reduce((acc, slug) => {
    const entry = DOCS[slug];
    const variant = entry[l] || entry[DEFAULT_LEGAL_LANG];
    acc[slug] = {
      slug,
      toc: entry.toc,
      lang: entry[l] ? l : DEFAULT_LEGAL_LANG,
      label: variant.label,
      title: variant.title,
      description: variant.description,
      markdown: fill(variant.markdown, entry[l] ? l : DEFAULT_LEGAL_LANG),
    };
    return acc;
  }, {});
}

/** Un document par slug et par langue. `null` si le slug est inconnu. */
export function getLegalDoc(slug, lang = DEFAULT_LEGAL_LANG) {
  if (!DOCS[slug]) return null;
  return docsFor(lang)[slug];
}

/**
 * Compat : les appelants qui ne connaissent pas la langue (Settings,
 * anciens imports) reçoivent le français, exactement comme avant.
 */
export const LEGAL_DOCS = docsFor(DEFAULT_LEGAL_LANG);

export default LEGAL_DOCS;
